import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Decimal from 'decimal.js';
import styled from 'styled-components';
import Panel from '../Panel';
import Break from '../Break';
import {
  BlackBorderButton,
  BlueBorderButton,
  BlueButton
} from 'components/Button';
import {
  SubPanel,
  SubPanelRow,
  PanelRowDescriptor,
  PanelRowTextForm,
  CoordinateInputContainer,
  PanelButtonRow,
  PanelRowDisplay,
  StyledForm,
  FullPanel,
  PanelContent,
  CurvePanelGraphContainer
} from 'components/PanelComponents';
import { createCurve, duplicateCurve } from '../../reducers/curve/actions';
import {
  setSidePanel,
  revertSidePanel,
  closeSidePanel
} from '../../reducers/sidePanel/actions';
import CurvePanelGraph from '../CurvePanelGraph';
import {
  API_FIELD_MAPPINGS,
  DB_INT11_MAX_FOR_CURVE_WITH_MULTIPLIER,
  DB_INT11_MIN_FOR_CURVE_WITH_MULTIPLIER,
  RESOURCE_TYPES,
  SIDEPANEL_STATES,
  CONTROL_TYPES,
  DB_UNDER_EXCITED,
  DB_OVER_EXCITED
} from './../../utils/appConstants';
import { ReactComponent as RemoveIcon } from '../../assets/remove-icon.svg';
import { determineBack } from 'utils/determineBack';
import { POLL_CURVES, subscribe, unsubscribe } from 'reducers/polling/actions';
import { TooltipRow } from 'components/Tooltip';
import { openErrorModal } from 'reducers/modal/actions';

const NO_OPTION_VALUE = 'None';

const RemoveButton = styled.button`
  background: white;
  padding-left: 8px;
`;

const SelectDefault = styled.option`
  color: #0d151f;
  opacity: 0.5;
`;

const Dropdown = styled.select`
  border: ${({ hasError }) =>
    hasError ? '1px solid #EA6E6D' : '1px solid #E5E7EB'};
  background-color: #ffffff;
  border-radius: 2px;
  width: 100%;
  height: 26px;
  // padding: 4px 0;
  font-size: 12px;
`;

const CoordinateInputWrapper = styled.div`
  display: flex;
  width: 68px;
  height: 24px;
  margin-right: 8px;
`;

const CoordinateInput = styled.input`
  border: ${({ hasError }) =>
    hasError ? '1px solid #EA6E6D' : '1px solid #E5E7EB'};
  width: 100%;
  outline: none;
`;

const CoordinateInputLabel = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #014c6b;
  color: #feffff;
  min-width: 18px;
  font-size: 12px;
`;

const YRefTypeText = styled.strong`
  font-size: 0.76em;
`;

const CurvePointsError = styled.div`
  color: #ea6e6d;
  font-size: 12px;
`;
const disabledRemoteMessage =
  'Only 10 Curve Points are available to be added to a curve';

const SUBSCRIBER_NAME = 'NewCurve';
const pollRate = parseInt(window.env.REACT_APP_GLOBAL_POLL_RATE_FREQUENCY);

const NewCurve = ({ isActive }) => {
  const dispatch = useDispatch();
  const data = useSelector(state => state.sidePanel.data);
  const sidePanel = useSelector(state => state.sidePanel);
  const [curveId, setCurveId] = useState(''); // string
  const [curveType, setCurveType] = useState(null); //int,
  const [setCurveTypeList, setSetCurveTypeList] = useState(null);
  const [setCurveTypeNameList, setSetCurveTypeNameList] = useState(null);
  const [curveDataArray, setCurveDataArray] = useState([
    //holds user input
    {
      xvalue: 0,
      yvalue: 0,
      excitation: 0,
      error: false
    }
  ]);
  const [curveDataArrayError, setCurveDataArrayError] = useState('');
  const [convertedDataArray, setConvertedDataArray] = useState([
    //holds converted user input for database
    {
      xvalue: 0,
      yvalue: 0,
      excitation: 0
    }
  ]);
  const [xMultiplier, setXMultiplier] = useState(0); // int (Default value is wanted as 0)
  const [yMultiplier, setYMultiplier] = useState(0); // int (Default value is wanted as 0)
  const [yRefType, setYRefType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreateCurveError, setIsCreateCurveError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [curveIdError, setCurveIdError] = useState(false);
  const [curveTypeError, setCurveTypeError] = useState(false);
  const [xMultiplierError, setXMultiplierError] = useState(false);
  const [yMultiplierError, setYMultiplierError] = useState(false);
  const [yRefTypeError, setYRefTypeError] = useState(false);
  const curves = useSelector(state => state.curve.curves);

  useEffect(() => {
    if (isActive) {
      dispatch(
        subscribe({
          subscriber: SUBSCRIBER_NAME,
          pollTypes: [
            {
              type: POLL_CURVES
            }
          ],
          pollRate
        })
      );
    } else {
      dispatch(unsubscribe(SUBSCRIBER_NAME));
    }
    return () => {
      if (isActive) {
        dispatch(unsubscribe(SUBSCRIBER_NAME));
      }
    };
  }, [dispatch, isActive]);
  useEffect(() => {
    if (data && data.curveType !== undefined) {
      const curveTypeName = CONTROL_TYPES[data.curveType].name;
      const curveType = CONTROL_TYPES[data.curveType].curveType;
      setSetCurveTypeList(curveType);
      setSetCurveTypeNameList(curveTypeName);
      setCurveType(curveType);
    }
    setIsLoading(false);
  }, [data]);

  useEffect(() => {
    if (parseInt(curveType) >= 0) {
      const yRefTypeKeys = Object.keys(
        API_FIELD_MAPPINGS.CURVE.type[curveType].yRefTypes
      );
      // if the selected curveType has only one yRefType, set
      if (yRefTypeKeys.length === 1) {
        setYRefType(yRefTypeKeys[0]);
      }
    }
  }, [curveType]);

  const handleCurveTypeChange = value => {
    setYRefType(null);
    setCurveType(value);
    if (curveType) {
      const yRefTypeKeys = Object.keys(
        API_FIELD_MAPPINGS.CURVE.type[curveType].yRefTypes
      );
      // if the selected curveType has only one yRefType, set
      if (yRefTypeKeys.length === 1) {
        setYRefType(yRefTypeKeys[0]);
      }
    }
  };

  const handleCurveIdChange = value => {
    setCurveId(value);
  };

  const handleYRefTypeChange = value => {
    setYRefType(value);
  };

  const handleInputChange = event => {
    const target = event.target;
    const value = target.value === NO_OPTION_VALUE ? null : target.value;
    const name = target.name;

    switch (name) {
      case 'curveType':
        handleCurveTypeChange(value);
        break;
      case 'curveId':
        handleCurveIdChange(value);
        break;
      case 'yRefType':
        handleYRefTypeChange(value);
        break;
      default:
        break;
    }
  };

  //Returns the length of a number string without the trailing zeros
  const checkDecimalLength = num => {
    if (num) {
      for (let i = num.length - 1; i >= 0; i--) {
        if (num[i] !== '0') {
          return i + 1;
        }
      }
    }
    return 0;
  };

  /**
   * For any X or Y change, check if there is a new multiplier.
   * This multiplier is only for storing numbers in the database with as little loss as possible.
   * This will check both axes and create a new convertedCurveData array for use in the database.
   * @param  {array} curveDataArray
   * @return {object}
   */
  const checkForMultiplier = curveDataArray => {
    const DB_MAX_NUM = 2147483647;
    const returnObj = {
      xMultiplier: 0,
      yMultiplier: 0,
      convertedDataArray: curveDataArray.slice()
    };

    //work with both axes
    ['x', 'y'].forEach(axis => {
      //Keep track of number with the longest decimal
      let largestPointValue = new Decimal(0);
      let smallestPOTM = null;
      for (const point of curveDataArray) {
        let pointNum = 0;
        if (typeof point[`${axis}value`] === 'number') {
          pointNum = point[`${axis}value`];
        } else {
          point.error = true;
          setIsCreateCurveError(true);
        }

        if (pointNum === 0) {
          continue;
        }
        let decimalPoint = new Decimal(pointNum);
        decimalPoint = decimalPoint.absoluteValue();
        let curvePointRemainder = decimalPoint.modulo(1);
        let powerOfTenMultiplier = 0;
        if (curvePointRemainder.isZero()) {
          // it's a whole number
          let currentPOTM = 0;
          let potmMultiplied = Math.pow(10, currentPOTM);
          let moduloResult = decimalPoint.modulo(potmMultiplied);
          while (moduloResult.equals(0)) {
            currentPOTM++;
            potmMultiplied = Math.pow(10, currentPOTM);
            moduloResult = decimalPoint.modulo(potmMultiplied);
          }
          // at this point moduloResult should be !== 0.
          // we want previous power of ten multiplier
          powerOfTenMultiplier = currentPOTM - 1;
        } else {
          //remainder is not zero, it's a fraction
          let currentPOTM = -1;
          let potmMultiplied = Math.pow(10, currentPOTM);
          let moduloResult = decimalPoint.modulo(potmMultiplied);
          while (!moduloResult.equals(0)) {
            currentPOTM--;
            potmMultiplied = Math.pow(10, currentPOTM);
            moduloResult = decimalPoint.modulo(potmMultiplied);
          }
          // at this point moduloResult should be !== 0.
          // we want previous power of ten multiplier
          powerOfTenMultiplier = currentPOTM;
        }

        // Now, our powerOfTenMultiplier is set. We need: the smallest multiplier
        // and the largest value
        if (smallestPOTM === null || powerOfTenMultiplier < smallestPOTM) {
          smallestPOTM = powerOfTenMultiplier;
        }

        if (decimalPoint.greaterThan(largestPointValue)) {
          largestPointValue = decimalPoint;
        }
      }

      // we made it through that loop without setting a multiplier,
      // set a default
      if (smallestPOTM === null) {
        smallestPOTM = 0;
      }

      // now we have the greatest value and smallest multiplier.
      // we need to divide by the multiplier to get the value.
      // With a negative multiplier, this is like multiplying by a positive
      let valuePreMultiplier = largestPointValue.dividedBy(
        Math.pow(10, smallestPOTM)
      );
      while (valuePreMultiplier.greaterThan(DB_MAX_NUM)) {
        smallestPOTM++;
        valuePreMultiplier = largestPointValue.dividedBy(
          Math.pow(10, smallestPOTM)
        );
        valuePreMultiplier = valuePreMultiplier.floor();
      }

      // now our smallestPOTM should be the correct one for our minimum.
      // now go through all of the curveDataArray
      //Set the x or y multiplier.
      returnObj[`${axis}Multiplier`] = smallestPOTM;

      //Convert the data into a database object
      //Safeguard against non-numbers and empty input
      returnObj.convertedDataArray = returnObj.convertedDataArray.map(item => {
        let returnValue = new Decimal(0);
        if (typeof item[`${axis}value`] === 'number') {
          returnValue = new Decimal(item[`${axis}value`]);
          returnValue = returnValue.dividedBy(Math.pow(10, smallestPOTM));
          returnValue = returnValue.floor();
        }
        return {
          ...item,
          [`${axis}value`]: returnValue.toNumber()
        };
      });
    });

    //Return the new multipliers and the new converted data
    return returnObj;
  };

  const handleXyChange = event => {
    const target = event.target;
    const value = target.value === NO_OPTION_VALUE ? null : target.value;
    const name = target.name;
    const axis = name.split('_')[0];
    const index = parseInt(name.split('_')[1]);

    const changingCoord = curveDataArray[index];
    const convertedChangingCoord = convertedDataArray[index];
    if (axis === 'x') {
      changingCoord.xvalue = isNaN(parseFloat(value)) ? '' : parseFloat(value);
    } else if (axis === 'y') {
      changingCoord.yvalue = isNaN(parseFloat(value)) ? '' : parseFloat(value);
    } else if (axis === 'excitation') {
      changingCoord.excitation = parseInt(value);
      convertedChangingCoord.excitation = parseInt(value);
    }

    if (changingCoord.error || isCreateCurveError) {
      changingCoord.error = false;
      setIsCreateCurveError(false);
    }

    //Don't mutate state directly
    const newCurveDataArray = curveDataArray.slice();
    const newConvertedDataArray = convertedDataArray.slice();
    newCurveDataArray[index] = changingCoord;
    newConvertedDataArray[index] = convertedChangingCoord;

    let multiplier = '';

    //If a curve point is changed, check for a new multiplier.
    if (axis !== 'excitation') {
      multiplier = checkForMultiplier(newCurveDataArray);
      setCurveDataArray(newCurveDataArray);
      setXMultiplier(multiplier.xMultiplier);
      setYMultiplier(multiplier.yMultiplier);
      setConvertedDataArray(multiplier.convertedDataArray);
    }
    //Otherwise, set the state with the new excitation change for both arrays of curve data.
    else {
      setCurveDataArray(newCurveDataArray);
      setConvertedDataArray(newConvertedDataArray);
    }
  };

  const removeExcitation = curveDataArray => {
    return curveDataArray.map(({ excitation, ...otherValues }) => otherValues);
  };

  const removeError = curveDataArray => {
    return curveDataArray.map(({ error, ...otherValues }) => otherValues);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);

    // TODO: Polish front-end validation

    const alreadyExists = curves.some(element => element.curveId === curveId);
    if (alreadyExists) {
      setIsSubmitting(false);
      return dispatch(duplicateCurve(curveId));
    }

    let finalDataArray = convertedDataArray;
    if (parseInt(curveType) !== CONTROL_TYPES.opModWattPFCurveId.curveType) {
      finalDataArray = removeExcitation(convertedDataArray);
    } else {
      //convert excitation to boolean
      finalDataArray = finalDataArray.map(curvePointInfo => {
        const updatedCurvePoint = {
          ...curvePointInfo
        };
        updatedCurvePoint.excitation =
          curvePointInfo.excitation === DB_UNDER_EXCITED;
        return updatedCurvePoint;
      });
    }

    try {
      if (
        validateId() &&
        validateCurveType() &&
        validateMultipliers() &&
        validateYRefType() &&
        validateCurvePoints()
      ) {
        finalDataArray = removeError(finalDataArray);

        const response = await dispatch(
          createCurve({
            curveId,
            curveType: parseInt(curveType),
            curveDataArray: finalDataArray,
            xMultiplier: parseInt(xMultiplier),
            yMultiplier: parseInt(yMultiplier),
            yRefType: parseInt(yRefType)
          })
        );
        if (setCurveTypeList >= 0) {
          dispatch(revertSidePanel({ curveId }));
        } else {
          dispatch(
            setSidePanel({
              entity: RESOURCE_TYPES.CURVE,
              state: SIDEPANEL_STATES.READ,
              id: response.curveId
            })
          );
        }
      } else {
        dispatch(
          openErrorModal('Could not create curve. Please check your submission')
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateId = () => {
    let passed = true;
    if (!curveId && curveId.length < 1) {
      setCurveIdError(true);
      passed = false;
    } else {
      setCurveIdError(false);
    }
    return passed;
  };

  const validateCurveType = () => {
    let passed = true;
    if (!curveType && curveType !== 0) {
      setCurveTypeError(true);
      passed = false;
    } else {
      setCurveTypeError(false);
    }
    return passed;
  };

  const validateMultipliers = () => {
    let passed = true;
    if (xMultiplier !== null && (xMultiplier > 9 || xMultiplier < -9)) {
      setXMultiplierError(true);
      passed = false;
    } else {
      setXMultiplierError(false);
    }

    if (yMultiplier !== null && (yMultiplier > 9 || yMultiplier < -9)) {
      setYMultiplierError(true);
      passed = false;
    } else {
      setYMultiplierError(false);
    }
    return passed;
  };

  const validateYRefType = () => {
    let passed = true;
    if (!yRefType && yRefType !== 0) {
      setYRefTypeError(true);
      passed = false;
    } else {
      setYRefTypeError(false);
    }
    return passed;
  };

  /**
   * Checks for repeating x,y values in Curve Points
   * @return {boolean} true|false validation pass
   */
  const validateCurvePoints = () => {
    let passed = true;
    const duplicateIndicesArray = findDuplicateXYValueIndices();
    if (duplicateIndicesArray.length > 0) {
      passed = false;
      const updatedCurveDataArray = curveDataArray.map((curveData, index) => {
        const curveDataCopy = { ...curveData };
        if (duplicateIndicesArray.indexOf(index) > -1) {
          curveDataCopy.error = true;
        }
        return curveDataCopy;
      });
      setCurveDataArray(updatedCurveDataArray);
      setCurveDataArrayError('Update or remove duplicate curve points');
    }
    return passed;
  };

  /**
   * Iterate through this.state.curveDataArray and compose ledger object to hold
   * repeating x,yvalue indices. From object flatten out all repeating x,y indices
   * into one dimentionsal return array.
   * @return {array} list of repeating indices in this.state.curveDataArray
   */
  const findDuplicateXYValueIndices = () => {
    const xValues = curveDataArray.map(({ xvalue }) => xvalue);
    const yValues = curveDataArray.map(({ yvalue }) => yvalue);
    let duplicateIndices = [];
    const duplicateLedger = {};
    // create dictionary of xvalue as key, value object containing yvalue key, value as array of
    // either duplicate indices or single if not duplicate
    // ie { 'xvalue': { 'yvalue': [0, 4], 'yvalue2' : [1] }} - xvalue, yvalue is duplicated at index 0, 4
    xValues.forEach((value, index) => {
      if (value in duplicateLedger) {
        if (yValues[index] in duplicateLedger[value]) {
          duplicateLedger[value][yValues[index]].push(index);
        } else {
          duplicateLedger[value][yValues[index]] = [index];
        }
      } else {
        duplicateLedger[value] = { [yValues[index]]: [index] };
      }
    });
    // Flatten out `duplicateLedger` object into 1 dimensional array
    for (const xValueMatch of Object.values(duplicateLedger)) {
      for (const xyDupIndices of Object.values(xValueMatch)) {
        if (xyDupIndices.length > 1) {
          duplicateIndices = [...duplicateIndices, ...xyDupIndices];
        }
      }
    }
    return duplicateIndices;
  };

  const addPoint = e => {
    e.preventDefault();
    const newCurveDataArray = curveDataArray.slice();
    const newConvertedData = convertedDataArray.slice();
    newCurveDataArray.push({
      xvalue: 0,
      yvalue: 0,
      excitation: 0
    });
    newConvertedData.push({
      xvalue: 0,
      yvalue: 0,
      excitation: 0
    });
    setCurveDataArray(newCurveDataArray);
    setConvertedDataArray(newConvertedData);
  };

  const removePoint = (e, index) => {
    e.preventDefault();
    const newCurveDataArray = curveDataArray.slice();
    newCurveDataArray.splice(index, 1);
    const multObj = checkForMultiplier(newCurveDataArray);
    setCurveDataArray(newCurveDataArray);
    setConvertedDataArray(multObj.convertedDataArray);
    setXMultiplier(multObj.xMultiplier);
    setYMultiplier(multObj.yMultiplier);
  };

  if (!isActive || (isActive && isLoading)) {
    return null;
  } else {
    return (
      <StyledForm onSubmit={handleSubmit}>
        <FullPanel>
          <Panel title="New Curve" primaryHeader />
          <PanelContent>
            <CurvePanelGraphContainer>
              <CurvePanelGraph
                curveData={convertedDataArray}
                xAxisLabel={
                  curveType
                    ? API_FIELD_MAPPINGS.CURVE.type[curveType].xLabel
                    : 'Select Curve Type'
                }
                yAxisLabel={
                  !(curveType && yRefType)
                    ? 'Select Y Reference Type'
                    : parseInt(yRefType) === 0 // N/A yRefType label
                    ? API_FIELD_MAPPINGS.CURVE.type[curveType].yLabel
                    : API_FIELD_MAPPINGS.CURVE.type[curveType].yRefTypes[
                        yRefType
                      ]
                }
                xMultiplier={xMultiplier}
                yMultiplier={yMultiplier}
              />
            </CurvePanelGraphContainer>
            <Break margin="16px 0" />

            <SubPanel>
              <SubPanelRow>
                <PanelRowDescriptor>ID</PanelRowDescriptor>
                <PanelRowTextForm
                  name="curveId"
                  onChange={handleInputChange}
                  hasError={curveIdError}
                  type="text"
                  minLength="1"
                  maxLength="64"
                  pattern="[a-zA-Z0-9_.~-]*$"
                  required
                />
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>Curve Type</PanelRowDescriptor>
                {setCurveTypeNameList ? (
                  <PanelRowDisplay>{setCurveTypeNameList}</PanelRowDisplay>
                ) : (
                  <Dropdown
                    name="curveType"
                    onChange={handleInputChange}
                    hasError={curveTypeError}
                  >
                    <SelectDefault value={NO_OPTION_VALUE} defaultValue>
                      Select One
                    </SelectDefault>
                    {Object.keys(API_FIELD_MAPPINGS.CURVE.type).map(function (
                      key,
                      index
                    ) {
                      return (
                        <option key={key} value={key}>
                          {API_FIELD_MAPPINGS.CURVE.type[key].displayText}
                        </option>
                      );
                    })}
                  </Dropdown>
                )}
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>Y Reference Type</PanelRowDescriptor>
                {curveType === null ||
                Object.keys(API_FIELD_MAPPINGS.CURVE.type[curveType].yRefTypes)
                  .length > 1 ? (
                  <Dropdown
                    name="yRefType"
                    onChange={handleInputChange}
                    hasError={yRefTypeError}
                  >
                    <SelectDefault value={NO_OPTION_VALUE} defaultValue>
                      Select One
                    </SelectDefault>
                    {curveType
                      ? Object.keys(
                          API_FIELD_MAPPINGS.CURVE.type[curveType].yRefTypes
                        ).map(function (key, index) {
                          return (
                            <option key={key} value={key}>
                              {
                                API_FIELD_MAPPINGS.CURVE.type[curveType]
                                  .yRefTypes[key]
                              }
                            </option>
                          );
                        })
                      : null}
                  </Dropdown>
                ) : (
                  <YRefTypeText>
                    {
                      API_FIELD_MAPPINGS.CURVE.type[curveType].yRefTypes[
                        yRefType
                      ]
                    }
                  </YRefTypeText>
                )}
              </SubPanelRow>
            </SubPanel>

            <Break margin="16px 0" />

            <SubPanel>
              <SubPanelRow>
                <PanelRowDescriptor>Curve Points</PanelRowDescriptor>
              </SubPanelRow>
              <CurvePointsError>
                {curveDataArrayError !== '' && curveDataArrayError}
              </CurvePointsError>
              {curveDataArray.map((coords, index) => {
                return (
                  <SubPanelRow key={index}>
                    <CoordinateInputContainer>
                      <CoordinateInputWrapper>
                        <CoordinateInputLabel>X</CoordinateInputLabel>
                        <CoordinateInput
                          type="number"
                          required
                          name={`x_${index}`}
                          onChange={handleXyChange}
                          value={coords.xvalue}
                          hasError={coords.error}
                          min={DB_INT11_MIN_FOR_CURVE_WITH_MULTIPLIER}
                          max={DB_INT11_MAX_FOR_CURVE_WITH_MULTIPLIER}
                          step="any"
                        />
                      </CoordinateInputWrapper>
                      <CoordinateInputWrapper>
                        <CoordinateInputLabel>Y</CoordinateInputLabel>
                        <CoordinateInput
                          type="number"
                          required
                          name={`y_${index}`}
                          onChange={handleXyChange}
                          value={coords.yvalue}
                          hasError={coords.error}
                          min={DB_INT11_MIN_FOR_CURVE_WITH_MULTIPLIER}
                          max={DB_INT11_MAX_FOR_CURVE_WITH_MULTIPLIER}
                          step="any"
                        />
                      </CoordinateInputWrapper>
                    </CoordinateInputContainer>
                    <Dropdown
                      name={`excitation_${index}`}
                      onChange={handleXyChange}
                      hidden={
                        parseInt(curveType) !==
                        CONTROL_TYPES.opModWattPFCurveId.curveType
                          ? true
                          : false
                      }
                    >
                      <option value={DB_OVER_EXCITED}>Over-excited</option>
                      <option value={DB_UNDER_EXCITED}>Under-excited</option>
                    </Dropdown>
                    {curveDataArray.length > 1 ? (
                      <RemoveButton
                        type="button"
                        onClick={e => removePoint(e, index)}
                      >
                        <RemoveIcon fill="#0779af" width="15" height="15" />
                      </RemoveButton>
                    ) : null}
                  </SubPanelRow>
                );
              })}

              <SubPanelRow>
                <TooltipRow
                  disabled={
                    curveDataArray.length && convertedDataArray.length >= 10
                  }
                  message={disabledRemoteMessage}
                >
                  <BlackBorderButton
                    onClick={addPoint}
                    type="button"
                    disabled={
                      curveDataArray.length && convertedDataArray.length >= 10
                    }
                  >
                    + Add Curve Point
                  </BlackBorderButton>
                </TooltipRow>
              </SubPanelRow>
            </SubPanel>
          </PanelContent>
          <PanelButtonRow>
            <BlueBorderButton
              type="button"
              onClick={() =>
                determineBack(
                  sidePanel,
                  () => dispatch(revertSidePanel()),
                  () => dispatch(closeSidePanel())
                )
              }
            >
              Cancel
            </BlueBorderButton>
            <BlueButton
              type="submit"
              disabled={isSubmitting || isCreateCurveError}
            >
              Create Curve
            </BlueButton>
          </PanelButtonRow>
        </FullPanel>
      </StyledForm>
    );
  }
};

export default NewCurve;
